import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import { Workflow } from '../../types/workflow/workflow.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export interface SaveWorkflowRequest {
  campaignId: string;
  draftFlow: {
    nodes: any[];
    edges: any[];
  };
}

export interface UpdateWorkflowRequest {
  campaignId: string;
  draftFlow: {
    nodes: any[];
    edges: any[];
  };
}

export interface PublishWorkflowRequest {
  campaignId: string;
  flow: {
    nodes: any[];
    edges: any[];
  };
}

// Save workflow as draft
export const saveWorkflow = async (data: SaveWorkflowRequest): Promise<Workflow> => {
  try {
    const response = await api.post('/workflows/save', data);
    return response.data;
  } catch (error) {
    console.error('Error saving workflow:', error);
    throw error;
  }
};

// Update workflow
export const updateWorkflow = async (data: UpdateWorkflowRequest): Promise<Workflow> => {
  try {
    const { campaignId, draftFlow } = data;
    const response = await api.put(`/workflows/${campaignId}`, {
      draftFlow,
    });
    return response.data?.workflow;
  } catch (error) {
    console.error('Error updating workflow:', error);
    throw error;
  }
};

// Publish workflow
export const publishWorkflow = async (data: PublishWorkflowRequest): Promise<Workflow> => {
  try {
    const response = await api.post('/workflows/publish', data);
    return response.data;
  } catch (error) {
    console.error('Error publishing workflow:', error);
    throw error;
  }
};

// Get workflow by campaign ID
export const getWorkflowByCampaignId = async (campaignId: string): Promise<Workflow> => {
  try {
    const response = await api.get(`/workflows/${campaignId}`);
    return response.data?.workflow;
  } catch (error) {
    console.error('Error fetching workflow:', error);
    throw error;
  }
};

export default api;
